// NOTE: This is a tsx not just ts file to support markup in the column definitions
import { ColDef, CellStyle } from "ag-grid-community";
import { TableRow, BaseColumnField } from "./types";
import { Tooltip, Skeleton } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { formatDateTime } from "../../../helpers/time";
import WarningIcon from "../../common/WarningIcon";
import { Animal } from "../../../redux/animals/animals-types";

// Base cell style used across the grid so column-specific cellStyle doesn't
// accidentally override padding/spacing from defaultColDef.
export const BASE_CELL_STYLE: CellStyle = { padding: "2px 4px" };

export const skeleton = <Skeleton variant="text" width="100%" height={8} sx={{ paddingTop: 3 }} />;

const animalCellRenderer = params => {
  if (params.value === undefined) {
    return skeleton;
  }
  if (params.data?.animal_conflict) {
    const animalNames = params.data?.animals.map((a: Animal) => {
      if (a.name) {
        return a.name;
      } else {
        return "Unnamed Animal";
      }
    });
    const toolTip = `Multiple animals tagged at this time (${animalNames.join(", ")})`;
    return <WarningIcon tooltip={toolTip} />;
  }
  return <span>{params.value}</span>;
};

export const defaultCellRenderer = params => {
  if (params.value === undefined) {
    return skeleton;
  }
  if (params.colDef.valueFormatter) {
    return params.colDef.valueFormatter(params);
  }
  return params.value;
};

export const baseColumnDefs: ColDef<TableRow>[] = [
  {
    field: "is_time_corrected",
    headerName: "",
    width: 35,
    sortable: false,
    resizable: false,
    lockPosition: true,
    cellRenderer: params => {
      if (params.value === false) {
        return (
          <Tooltip title={"Time correction not available, showing device time"}>
            <AccessTimeIcon color="warning" />
          </Tooltip>
        );
      }
      return "";
    },
  },
  {
    field: "time_utc",
    headerName: "Time",
    valueFormatter: params => formatDateTime(params.value),
    width: 190,
    cellStyle: (params: any) => {
      // keep the same padding as defaultColDef so vertical alignment matches other cells
      // highlight when the row is showing device time (i.e. correction not available)
      if (params?.data && params.data.is_time_corrected === false) {
        return {
          ...BASE_CELL_STYLE,
          // slightly more orange than the previous soft yellow
          backgroundColor: "rgba(255, 183, 77, 0.18)",
        };
      }
      return BASE_CELL_STYLE;
    },
  },
  {
    field: "time_correction_sec",
    headerName: "Time Correction (sec)",
    sortable: false,
    width: 160,
  },
  {
    field: "device_time_utc",
    headerName: "Device Time",
    valueFormatter: params => formatDateTime(params.value),
    width: 190,
  },
  {
    field: "full_id",
    headerName: "Full ID",
    width: 165,
  },
  {
    field: "serial",
    headerName: "Receiver Serial",
    width: 145,
  },
  {
    field: "sensor_value",
    headerName: "Sensor Value",
    sortable: false,
    width: 135,
  },
  {
    field: "sensor_unit",
    headerName: "Sensor Unit",
    sortable: false,
    width: 115,
  },
  {
    field: "animal_name",
    headerName: "Animal Name",
    sortable: false,
    width: 160,
    cellRenderer: animalCellRenderer,
  },
  {
    field: "animal_species",
    headerName: "Species",
    sortable: false,
    width: 160,
    cellRenderer: animalCellRenderer,
  },
  {
    field: "file_names",
    headerName: "File(s)",
    sortable: false,
    width: 350,
    valueFormatter: params => (params.value ? params.value.join(", ") : ""),
    tooltipValueGetter: params => params.value,
  },
];

export const allColumnIds: BaseColumnField[] = baseColumnDefs.map(
  col => col.field as BaseColumnField
);
export const ltcDefaultHiddenCol: BaseColumnField[] = ["device_time_utc"];
export const animalFeatCols: BaseColumnField[] = ["animal_name", "animal_species"];
export const ltcFeatCols: BaseColumnField[] = [
  "is_time_corrected",
  "time_utc",
  "time_correction_sec",
]; // Order match default order in table
